import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2 } from 'react-feather';
import ErrorPage403 from '@/app/routes/errorPage403';
import cancelIllustrationUrl from '@/assets/svg/cancel-request-illustration.svg?url';
import filePdfIconUrl from '@/assets/svg/FilePDF.svg?url';
import fileJpgIconUrl from '@/assets/svg/FileJPG.svg?url';
import fileJpegIconUrl from '@/assets/svg/FileJPEG.svg?url';
import filePngIconUrl from '@/assets/svg/FilePNG.svg?url';
import fileDocxIconUrl from '@/assets/svg/FileDOCX.svg?url';
import { Dialog } from '@/shared/components/Dialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import StepTabNav from '@/shared/components/StepTabNav';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Checkbox } from '@/shared/components/Checkbox';
import { DatePickerField } from '@/shared/components/DatePickerField';
import { Snackbar } from '@/shared/components/Snackbar';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import UnitTextField from '../../components/UnitTextField';
import PriceCurrencyField from '../../components/PriceCurrencyField';
import GaugeDisplay from '../../components/GaugeDisplay';
import {
  FILL_TASK_FORM_LABELS as L,
  TASK_FORM_LABELS,
} from '../../constants/labels';
import {
  getFormSchema,
  submitTaskForm,
  generateSignaturePresignedUrls,
  generateProfileSignaturePresignedUrl,
  updateProfileSignature,
  generateAttachmentPresignedUrls,
  deleteProvisionalAttachment,
  type SignatureMedia,
  type TechnicianProfileSignature,
} from '../../api/fillTaskFormApiService';
import { fetchProfile } from '@/epics/userProfile/api/viewProfileApiService';
import { uploadFileToS3 } from '@/shared/utils/uploadFileToS3';
import type { EquipmentListItem } from '@/epics/facilityManagement/api/listEquipmentApiService';
import { EquipmentDropdown } from './EquipmentDropdown';
import ClientSignatureCanvas from './ClientSignatureCanvas';
import type {
  FormField,
  FormSection,
  FieldType,
  TaskFormApiSection,
  TaskFormApiField,
  TaskFormApiFieldValidation,
} from '../../types';

// ─── File type icon helper ────────────────────────────────────────────────────

const FILE_TYPE_ICONS: Record<string, string> = {
  JPG: fileJpgIconUrl,
  JPEG: fileJpegIconUrl,
  PNG: filePngIconUrl,
  PDF: filePdfIconUrl,
  DOC: fileDocxIconUrl,
  DOCX: fileDocxIconUrl,
  XLS: fileDocxIconUrl,
  XLSX: fileDocxIconUrl,
  CSV: fileDocxIconUrl,
  TXT: fileDocxIconUrl,
};

function getFileIconUrl(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() ?? '';
  return FILE_TYPE_ICONS[ext] ?? filePdfIconUrl;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type FillStep = 1 | 2 | 3;

/**
 * Successfully uploaded attachment file. Matches the backend `AttachmentValue`
 * shape stored in the `field_responses` JSONB column.
 */
interface AttachmentValue {
  filename: string;
  s3_key: string;
  size: number;
  mime_type: string;
}

/** Values keyed by field ID. Stored as raw UI state (strings, arrays, Date objects). */
type FieldValues = Record<string, unknown>;

/** Navigation state passed from ViewServiceRequest via useNavigate state. */
export interface FillTaskFormNavState {
  serviceRequestDisplayId?: string;
  clientName?: string;
  equipmentName?: string | null;
  technicianName?: string | null;
  scheduledStartAt?: string | null;
  formName?: string;
  /** UUID primary key of the form template (`form_templates.id`). */
  formId?: string;
  /** Business ID of the form template (e.g. "FORM000000123"). */
  formDisplayId?: string;
  /** Technician's business ID (e.g. "TECH00000001"). */
  technicianId?: string | null;
  /** Business display ID (e.g. "CLIENT000001") — shown in "ID klienta" field. */
  clientId?: string;
  /** Internal UUID (clients.id) — used for the equipment list API call. */
  clientUuid?: string | null;
  /** Business display ID (e.g. "FAC000001") — shown in "ID placówki" field. */
  facilityId?: string | null;
  /** Internal UUID (facilities.id) — used for the equipment list API call. */
  facilityUuid?: string | null;
  facilityName?: string | null;
  facilityAddressLine1?: string | null;
  facilityAddressLine2?: string | null;
  facilityCity?: string | null;
  facilityState?: string | null;
  facilityPostalCode?: string | null;
  facilityCountry?: string | null;
  /** Equipment display ID from the service request (e.g. "EQUIP0000001"). */
  equipmentId?: string | null;
  /** Internal UUID (equipments.id) — used for reliable list matching. */
  equipmentUuid?: string | null;
  /** Equipment raw category code (e.g. "imaging"). */
  equipmentCategory?: string | null;
  /** Equipment human-readable category label (e.g. "Imaging"). */
  equipmentCategoryLabel?: string | null;
  /** Equipment serial number. */
  equipmentSerialNumber?: string | null;
  /** Equipment manufacture date (ISO string or formatted). */
  equipmentDateOfProduction?: string | null;
  /** Equipment model name. */
  equipmentModelName?: string | null;
  /** Client phone number. */
  clientPhoneNumber?: string | null;
  /** Client email address. */
  clientEmail?: string | null;
}

export interface FillTaskFormProps {
  /** Internal UUID of the service request (`service_requests.id`). */
  serviceRequestId: string;
  /** UUID primary key of the form template — required to load the field schema. */
  formId?: string;
  /** Optional SR metadata pre-filled from navigation state. */
  navState?: FillTaskFormNavState;
}

// ─── Schema mapping ────────────────────────────────────────────────────────────

const VALID_FIELD_TYPES = new Set<FieldType>([
  'text',
  'textarea',
  'number',
  'number_range',
  'date',
  'date_range',
  'dropdown',
  'radio',
  'checkbox',
  'attachment',
  'price',
]);

function mapApiFieldToFormField(apiField: TaskFormApiField): FormField {
  const { id, type, config, validationRules } = apiField;
  const rules: Partial<TaskFormApiFieldValidation> = validationRules ?? {};

  const isRangeNumber =
    type === 'number' && (rules.min != null || rules.max != null);
  const isRangeDate = type === 'date' && config.variant === 'range';

  const resolvedType: FieldType | '' = isRangeNumber
    ? 'number_range'
    : isRangeDate
      ? 'date_range'
      : VALID_FIELD_TYPES.has(type as FieldType)
        ? (type as FieldType)
        : '';

  const field: FormField = {
    id,
    type: resolvedType,
    label: config.label,
    required: rules.required ?? false,
  };

  if (config.uom) {
    field.unit = config.uom;
  }
  if (rules.maxLength != null) {
    field.maxLength = rules.maxLength;
  }
  if (rules.min != null) {
    field.min = rules.min;
  }
  if (rules.max != null) {
    field.max = rules.max;
  }
  if (config.multiSelect) {
    field.multiSelect = config.multiSelect;
  }

  if (config.options && config.options.length > 0) {
    field.options = config.options.map((o, i) => ({
      id: `${o.value}_${i}`,
      label: o.label,
      value: o.value,
    }));
  }

  if (config.acceptedFileTypes && config.acceptedFileTypes.length > 0) {
    field.acceptedFileTypes = config.acceptedFileTypes.map((t) =>
      t.startsWith('.') ? t.slice(1).toUpperCase() : t.toUpperCase()
    );
  }

  if (config.maxFileSizeMb != null) {
    field.maxFileSizeMb = config.maxFileSizeMb;
  }
  if (config.maxFiles != null) {
    field.maxFiles = config.maxFiles;
  }

  return field;
}

function mapApiSectionsToFormSections(
  apiSections: TaskFormApiSection[]
): FormSection[] {
  return [...apiSections]
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      id: section.id,
      name: section.name,
      description: section.description ?? '',
      fields: [...section.fields]
        .filter((f) => f.type !== 'signature')
        .sort((a, b) => (a.uiOptions?.order ?? 0) - (b.uiOptions?.order ?? 0))
        .map(mapApiFieldToFormField),
    }));
}

// ─── Field value helpers ──────────────────────────────────────────────────────

function isFullWidthField(type: FieldType | ''): boolean {
  return (
    type === 'textarea' ||
    type === 'date_range' ||
    type === 'attachment' ||
    type === 'radio' ||
    type === 'checkbox' ||
    type === 'number_range'
  );
}

function formatDateDisplay(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function formatFieldValueForDisplay(
  field: FormField,
  rawValue: unknown
): string {
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return L.STEP3_FIELD_EMPTY;
  }

  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'price':
      return String(rawValue);

    case 'number':
    case 'number_range': {
      const str = String(rawValue);
      return field.unit ? `${str} ${field.unit}` : str;
    }

    case 'date': {
      if (rawValue instanceof Date) {
        return formatDateDisplay(rawValue);
      }
      return String(rawValue);
    }

    case 'date_range': {
      const range = rawValue as { start?: Date; end?: Date };
      const start = range.start ? formatDateDisplay(range.start) : '—';
      const end = range.end ? formatDateDisplay(range.end) : '—';
      return `${start} – ${end}`;
    }

    case 'dropdown':
    case 'radio': {
      const val = String(rawValue);
      const opt = field.options?.find((o) => o.value === val);
      return opt ? opt.label : val;
    }

    case 'checkbox': {
      const vals = rawValue as string[];
      if (vals.length === 0) {
        return L.STEP3_FIELD_EMPTY;
      }
      return vals
        .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }

    case 'attachment':
      return typeof rawValue === 'string' ? rawValue : '—';

    default:
      return String(rawValue);
  }
}

function buildFieldInputs(
  sections: FormSection[],
  values: FieldValues,
  attachmentUploads: Record<string, AttachmentValue[]>
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      if (field.type === 'attachment') {
        const uploaded = attachmentUploads[field.id];
        if (uploaded && uploaded.length > 0) {
          inputs[field.id] = uploaded;
        }
        continue;
      }

      const val = values[field.id];
      if (val === undefined || val === null || val === '') {
        continue;
      }
      if (Array.isArray(val) && val.length === 0) {
        continue;
      }

      if (
        (field.type === 'number' || field.type === 'number_range') &&
        typeof val === 'string'
      ) {
        const n = parseFloat(val);
        inputs[field.id] = Number.isNaN(n) ? val : n;
      } else if (field.type === 'date' && val instanceof Date) {
        inputs[field.id] = val.toISOString().slice(0, 10);
      } else if (field.type === 'date_range') {
        const range = val as { start?: Date; end?: Date };
        inputs[field.id] = {
          start: range.start ? range.start.toISOString().slice(0, 10) : null,
          end: range.end ? range.end.toISOString().slice(0, 10) : null,
        };
      } else if (
        (field.type === 'dropdown' || field.type === 'radio') &&
        field.options
      ) {
        // fieldValues stores option.id (e.g. "passed_0"); resolve back to option.value
        if (Array.isArray(val)) {
          inputs[field.id] = val.map(
            (id) => field.options?.find((o) => o.id === id)?.value ?? id
          );
        } else {
          inputs[field.id] =
            field.options.find((o) => o.id === val)?.value ?? val;
        }
      } else {
        inputs[field.id] = val;
      }
    }
  }
  return inputs;
}

function validateStep2(
  sections: FormSection[],
  values: FieldValues,
  attachmentUploads: Record<string, AttachmentValue[]>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      if (!field.required) {
        continue;
      }
      if (field.type === 'attachment') {
        const uploaded = attachmentUploads[field.id];
        if (!uploaded || uploaded.length === 0) {
          errors[field.id] = L.ERROR_FIELD_REQUIRED;
        }
        continue;
      }
      const val = values[field.id];
      const isEmpty =
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0) ||
        (field.type === 'date_range' &&
          (!(val as { start?: Date }).start || !(val as { end?: Date }).end));
      if (isEmpty) {
        errors[field.id] =
          field.type === 'date_range'
            ? `${L.ERROR_DATE_RANGE_START} ${L.ERROR_DATE_RANGE_END}`
            : L.ERROR_FIELD_REQUIRED;
        continue;
      }
      // maxLength — text / textarea
      if (
        (field.type === 'text' || field.type === 'textarea') &&
        field.maxLength != null &&
        typeof val === 'string' &&
        val.length > field.maxLength
      ) {
        errors[field.id] = L.ERROR_FIELD_MAX_LENGTH.replace(
          '{max}',
          String(field.maxLength)
        );
        continue;
      }
      // min / max — number / number_range
      if (
        (field.type === 'number' || field.type === 'number_range') &&
        typeof val === 'string' &&
        val !== ''
      ) {
        const num = parseFloat(val);
        if (!Number.isNaN(num)) {
          if (field.max != null && num > field.max) {
            errors[field.id] = L.ERROR_FIELD_MAX_VALUE.replace(
              '{max}',
              String(field.max)
            );
            continue;
          }
          if (field.min != null && num < field.min) {
            errors[field.id] = L.ERROR_FIELD_MIN_VALUE.replace(
              '{min}',
              String(field.min)
            );
          }
        }
      }
    }
  }
  return errors;
}

function hasAnyValue(values: FieldValues): boolean {
  return Object.values(values).some((v) => {
    if (v === undefined || v === null || v === '') {
      return false;
    }
    if (Array.isArray(v)) {
      return v.length > 0;
    }
    if (typeof v === 'object' && v !== null) {
      return Object.values(v as Record<string, unknown>).some(
        (x) => x !== undefined && x !== null && x !== ''
      );
    }
    return true;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const FILL_FORM_STEPS = [
  { label: L.STEP_1_LABEL },
  { label: L.STEP_2_LABEL },
  { label: L.STEP_3_LABEL },
];

/** Single file row used in Step 3 and SubmittedView to preview uploaded files. */
const AttachmentFileRow: React.FC<{ file: File }> = ({ file }) => (
  <div className="flex items-center gap-3">
    <img
      src={getFileIconUrl(file.name)}
      alt=""
      aria-hidden="true"
      className="w-10 h-10 shrink-0"
    />
    <div className="flex flex-col min-w-0">
      <p className="text-sm text-[#2A4455] truncate">{file.name}</p>
      <p className="text-xs text-[#878B8E]">{formatFileSize(file.size)}</p>
    </div>
  </div>
);

/** Read-only key–value row used in Step 1 and Step 3. */
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1">
    <p className="text-xs font-normal text-[#474A4C]">{label}</p>
    <p className="text-sm font-normal text-[#181A1B] wrap-break-word">
      {value || L.VALUE_NOT_ASSIGNED}
    </p>
  </div>
);

/**
 * Read-only phone field for Step 1 matching DisabledTextField underline style.
 * Renders a split layout: country code (flag + +48) on the left with a right
 * separator, phone number on the right — matching the Figma design.
 */
const DisabledPhoneField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex min-w-0">
    {/* Country code — paddingLeft:16 paddingRight:10 matching Figma spec */}
    <div className="relative flex-shrink-0 rounded-tl-[4px]">
      <div className="absolute inset-0 bg-[#181A1B] opacity-[0.04] pointer-events-none rounded-tl-[4px]" />
      <div className="relative flex flex-col justify-center h-14 pl-4 pr-[10px] border-r border-b border-[#474A4C]">
        <p
          className="text-[#474A4C] font-normal whitespace-nowrap"
          style={{ fontSize: 12, lineHeight: '16px', letterSpacing: 0.4 }}
        >
          Numer kierunkowy
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <img src="/polandFlag.svg" alt="PL" className="w-[22px] h-[16px]" />
          <span
            className="text-[#181A1B] font-normal"
            style={{ fontSize: 16, lineHeight: '24px', letterSpacing: 0.5 }}
          >
            +48
          </span>
        </div>
      </div>
    </div>
    {/* Phone number section */}
    <div className="relative flex-1 min-w-0 rounded-tr-[4px]">
      <div className="absolute inset-0 bg-[#181A1B] opacity-[0.04] pointer-events-none rounded-tr-[4px]" />
      <div className="border-b border-[#181A1B] flex items-center h-14 pl-4 py-1">
        <div className="flex flex-col flex-1 min-w-0">
          <p
            className="text-[#474A4C] font-normal"
            style={{ fontSize: 12, lineHeight: '16px', letterSpacing: 0.4 }}
          >
            {label}
          </p>
          <p
            className="text-[#181A1B] font-normal truncate"
            style={{ fontSize: 16, lineHeight: '24px', letterSpacing: 0.5 }}
          >
            {value || L.VALUE_NOT_ASSIGNED}
          </p>
        </div>
      </div>
    </div>
  </div>
);

/** Material-Design-style disabled text field used in Step 1 read-only sections. */
const DisabledCalendarIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 2V6"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 2V6"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 10H21"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DisabledTextField: React.FC<{
  label: string;
  value: string;
  showCalendarIcon?: boolean;
}> = ({ label, value, showCalendarIcon }) => (
  <div className="relative flex flex-col rounded-[4px] overflow-hidden min-w-0">
    <div className="absolute inset-0 bg-[#181A1B] opacity-[0.04] pointer-events-none rounded-tl-[4px] rounded-tr-[4px]" />
    <div className="border-b border-[#181A1B] flex items-center h-14 pl-4 py-1">
      <div className="flex flex-col flex-1 min-w-0">
        <p className="text-[12px] leading-4 tracking-[0.4px] text-[#474A4C] font-normal">
          {label}
        </p>
        <p className="text-[16px] leading-6 tracking-[0.5px] text-[#181A1B] font-normal truncate">
          {value || L.VALUE_NOT_ASSIGNED}
        </p>
      </div>
      {showCalendarIcon && (
        <div className="w-12 h-12 flex items-center justify-center opacity-[0.38] pointer-events-none shrink-0">
          <DisabledCalendarIcon />
        </div>
      )}
    </div>
  </div>
);

// ─── FillField (controlled field for Step 2) ──────────────────────────────────

interface FillFieldProps {
  field: FormField;
  value: unknown;
  /** Stable callback — receives fieldId so parent can use a single memoized handler. */
  onFieldChange: (fieldId: string, val: unknown) => void;
  error?: string;
  /**
   * Real upload progress for attachment fields: filename → bytes loaded.
   * Provided by the parent while provisional S3 uploads are in flight.
   */
  uploadProgress?: Record<string, number>;
  /**
   * Filenames of attachment files that failed to upload. FileUpload renders a
   * retry icon for each.
   */
  failedFiles?: string[];
  /**
   * Called when the user clicks the retry icon for a failed attachment file.
   */
  onRetry?: (filename: string) => void;
  /**
   * Called when the file selection for an attachment field changes.
   * The parent uses this to trigger provisional S3 uploads on select.
   */
  onAttachmentFilesChanged?: (fieldId: string, files: File[]) => void;
}

/**
 * Controlled form field for the task-form fill wizard (Step 2).
 * Mirrors PreviewField but accepts value / onFieldChange props instead of managing its own state.
 */
const FillField: React.FC<FillFieldProps> = React.memo(
  ({
    field,
    value,
    onFieldChange,
    error,
    uploadProgress,
    failedFiles,
    onRetry,
    onAttachmentFilesChanged,
  }) => {
    const [fileError, setFileError] = useState('');

    const optLabel = !field.required ? (
      <>
        {field.label}{' '}
        <span
          style={{
            fontSize: 12,
            lineHeight: '16px',
            letterSpacing: '0.4px',
            color: '#474A4C',
          }}
        >
          ({TASK_FORM_LABELS.FIELD_OPTIONAL})
        </span>
      </>
    ) : (
      field.label
    );

    const textVal = typeof value === 'string' ? value : '';
    const dateVal = value instanceof Date ? value : undefined;
    const dateRangeVal = (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date)
        ? value
        : { start: undefined, end: undefined }
    ) as { start?: Date; end?: Date };
    const arrayVal = Array.isArray(value) ? (value as string[]) : [];
    const singleStrVal = typeof value === 'string' ? value : '';

    switch (field.type) {
      case 'text':
        return (
          <div className="flex flex-col gap-1">
            <TextField
              label={optLabel}
              value={textVal}
              onChange={(e) => {
                const v = field.maxLength
                  ? e.target.value.slice(0, field.maxLength)
                  : e.target.value;
                onFieldChange(field.id, v);
              }}
              fullWidth
              error={!!error}
              {...(field.maxLength != null
                ? { maxLength: field.maxLength }
                : {})}
            />
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );

      case 'price':
        return (
          <div className="flex flex-col gap-1">
            <PriceCurrencyField
              label={optLabel}
              value={textVal}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                onFieldChange(
                  field.id,
                  raw.match(/^\d{0,10}(\.\d{0,2})?/)?.[0] ?? ''
                );
              }}
              fullWidth
            />
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );

      case 'textarea': {
        const taMax = field.maxLength ?? 500;
        return (
          <div className="flex flex-col gap-1">
            <Textarea
              label={optLabel}
              value={textVal}
              onChange={(e) =>
                onFieldChange(field.id, e.target.value.slice(0, taMax))
              }
              maxLength={taMax}
              fullWidth
              rows={3}
              error={!!error}
            />
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );
      }

      case 'number':
        return (
          <div className="flex flex-col gap-1">
            <TextField
              label={optLabel}
              value={textVal}
              onChange={(e) => {
                const raw = e.target.value.replace(
                  field.min != null && field.min >= 0
                    ? /[^0-9.]/g
                    : /[^0-9.-]/g,
                  ''
                );
                const cleaned = raw.match(/^-?\d{0,10}(\.\d{0,2})?/)?.[0] ?? '';
                if (cleaned === '' || cleaned === '-') {
                  onFieldChange(field.id, cleaned);
                  return;
                }
                const num = parseFloat(cleaned);
                if (field.max != null && num > field.max) {
                  return;
                }
                onFieldChange(field.id, cleaned);
              }}
              onBlur={() => {
                if (textVal === '' || textVal === '-') {
                  return;
                }
                const num = parseFloat(textVal);
                if (field.min != null && num < field.min) {
                  onFieldChange(field.id, String(field.min));
                }
              }}
              fullWidth
              error={!!error}
              {...(field.unit
                ? {
                    trailingIcon: (
                      <span className="text-sm text-[#474A4C] pr-3">
                        {field.unit}
                      </span>
                    ),
                  }
                : {})}
            />
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );

      case 'number_range': {
        const numericValue = textVal === '' ? null : parseFloat(textVal);
        return (
          <div className="flex items-start gap-6">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <UnitTextField
                label={optLabel}
                value={textVal}
                onChange={(e) => {
                  const raw = e.target.value
                    .replace(
                      field.min != null && field.min >= 0
                        ? /[^0-9]/g
                        : /[^0-9-]/g,
                      ''
                    )
                    .slice(0, 10);
                  if (raw === '' || raw === '-') {
                    onFieldChange(field.id, raw);
                    return;
                  }
                  const num = parseFloat(raw);
                  if (field.max != null && num > field.max) {
                    return;
                  }
                  onFieldChange(field.id, raw);
                }}
                onBlur={() => {
                  if (textVal === '' || textVal === '-') {
                    return;
                  }
                  const num = parseFloat(textVal);
                  if (field.min != null && num < field.min) {
                    onFieldChange(field.id, String(field.min));
                  }
                }}
                unit={field.unit ?? ''}
                fullWidth
              />
              {error && <p className="text-xs text-[#E74141]">{error}</p>}
            </div>
            <div className="shrink-0">
              <GaugeDisplay
                value={numericValue}
                min={field.min}
                max={field.max}
                unit={field.unit}
              />
            </div>
          </div>
        );
      }

      case 'date':
        return (
          <DatePickerField
            label={optLabel}
            mode="single"
            value={dateVal}
            onChange={(d) => onFieldChange(field.id, d)}
            labelFontSize={16}
            error={!!error}
            {...(error ? { errorMessage: error } : {})}
          />
        );

      case 'date_range':
        return (
          <DatePickerField
            label={optLabel}
            mode="range"
            startDate={dateRangeVal.start}
            endDate={dateRangeVal.end}
            onRangeChange={(start, end) =>
              onFieldChange(field.id, { start, end })
            }
            labelFontSize={16}
            error={!!error}
            {...(error ? { errorMessage: error } : {})}
          />
        );

      case 'dropdown': {
        const items = (field.options ?? [])
          .filter((o) => o.label)
          .map((o) => ({ id: o.id, label: o.label }));
        const dropdownLabel = optLabel as React.ReactNode;

        if (field.multiSelect) {
          const selectedLabels = items
            .filter((i) => arrayVal.includes(i.id))
            .map((i) => i.label)
            .join(', ');
          const multiValue = selectedLabels || undefined;
          const multiTrigger = ({
            open,
            toggle,
          }: {
            open: boolean;
            toggle: () => void;
          }) => (
            <DropDownButton
              label={dropdownLabel}
              open={open}
              toggle={toggle}
              {...(multiValue !== undefined ? { value: multiValue } : {})}
            />
          );
          return (
            <div className="flex flex-col gap-1">
              <Dropdown
                items={items}
                selectedId={arrayVal}
                multiSelect
                showCheckboxes
                onItemClick={(id) =>
                  onFieldChange(
                    field.id,
                    arrayVal.includes(id)
                      ? arrayVal.filter((x) => x !== id)
                      : [...arrayVal, id]
                  )
                }
                menuFullWidth
                placement="bottom-start"
                autoMaxHeight
                error={!!error}
                trigger={multiTrigger}
              />
              {error && <p className="text-xs text-[#E74141]">{error}</p>}
            </div>
          );
        }

        const selectedLabel = items.find((i) => i.id === singleStrVal)?.label;
        const singleTrigger = ({
          open,
          toggle,
        }: {
          open: boolean;
          toggle: () => void;
        }) => (
          <DropDownButton
            label={dropdownLabel}
            open={open}
            toggle={toggle}
            {...(selectedLabel !== undefined ? { value: selectedLabel } : {})}
          />
        );
        return (
          <div className="flex flex-col gap-1">
            <Dropdown
              items={items}
              selectedId={singleStrVal ? [singleStrVal] : []}
              onItemClick={(id) => onFieldChange(field.id, id)}
              menuFullWidth
              placement="bottom-start"
              autoMaxHeight
              error={!!error}
              trigger={singleTrigger}
            />
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );
      }

      case 'radio': {
        const options = (field.options ?? []).filter((o) => o.label);
        return (
          <div className="flex flex-col gap-2">
            <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
            {options.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 py-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  checked={singleStrVal === opt.value}
                  onChange={() => onFieldChange(field.id, opt.value)}
                  className="w-4 h-4 accent-[#2A4455] cursor-pointer"
                />
                <span className="text-bodyMedium text-[#181A1B] align-middle">
                  {opt.label}
                </span>
              </label>
            ))}
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );
      }

      case 'checkbox': {
        const options = (field.options ?? []).filter((o) => o.label);
        return (
          <div className="flex flex-col gap-2">
            <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
            {options.map((opt) => (
              <Checkbox
                key={opt.id}
                label={opt.label}
                labelClassName="!text-bodyMedium !text-[#181A1B]"
                checked={arrayVal.includes(opt.value)}
                onChange={(checked) =>
                  onFieldChange(
                    field.id,
                    checked
                      ? [...arrayVal, opt.value]
                      : arrayVal.filter((x) => x !== opt.value)
                  )
                }
              />
            ))}
            {error && <p className="text-xs text-[#E74141]">{error}</p>}
          </div>
        );
      }

      case 'attachment': {
        const files = Array.isArray(value) ? (value as File[]) : [];
        const allowedTypes = (field.acceptedFileTypes ?? []).filter(
          (t): t is FileType => Object.values(FileType).includes(t as FileType)
        );
        const limitsHint = [
          field.maxFiles != null ? `Maks. ${field.maxFiles} plików` : null,
          field.maxFileSizeMb != null
            ? `${field.maxFileSizeMb} MB każdy`
            : null,
        ]
          .filter(Boolean)
          .join(', ');
        return (
          <div className="flex flex-col gap-1.5">
            <span className="text-bodyLarge text-[#474A4C]">{optLabel}</span>
            <FileUpload
              onFileSelect={(newFiles) => {
                onFieldChange(field.id, newFiles);
                onAttachmentFilesChanged?.(field.id, newFiles);
                if (newFiles.length > 0) {
                  setFileError('');
                }
              }}
              selectedFiles={files}
              allowedTypes={
                allowedTypes.length > 0 ? allowedTypes : Object.values(FileType)
              }
              {...(field.maxFiles != null ? { maxFiles: field.maxFiles } : {})}
              {...(field.maxFileSizeMb != null
                ? { maxFileSizeMb: field.maxFileSizeMb }
                : {})}
              onError={setFileError}
              error={Boolean(fileError || error)}
              errorMessage={fileError || error || undefined}
              uploadProgress={uploadProgress ?? {}}
              {...(failedFiles != null ? { failedFiles } : {})}
              {...(onRetry != null ? { onRetry } : {})}
              fullWidth
            />
            {limitsHint && (
              <p className="text-xs text-[#474A4C]">{limitsHint}</p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  }
);

// ─── Discard dialog ────────────────────────────────────────────────────────────

/**
 * Confirmation modal shown when the technician tries to leave the form
 * with unsaved changes. Matches the Figma "Popup_Discard Changes" design.
 */
const DiscardDialog: React.FC<{
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ onCancel, onConfirm }) => (
  <Dialog open onClose={onCancel} maxWidth={320} className="bg-white">
    <div className="flex flex-col items-center gap-6 p-6">
      <div className="w-[137px] h-[137px] shrink-0">
        <img
          src={cancelIllustrationUrl}
          alt=""
          aria-hidden="true"
          className="w-full h-full"
        />
      </div>

      <div className="flex flex-col gap-4 w-full text-center">
        <p
          className="text-[#474A4C]"
          style={{
            fontSize: 18,
            fontWeight: 400,
            lineHeight: '26px',
            fontFamily: 'Roboto',
          }}
        >
          {L.DISCARD_TITLE}
        </p>
        <p
          className="text-[#474A4C]"
          style={{
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.25px',
            fontFamily: 'Roboto',
          }}
        >
          {L.DISCARD_DESC}
        </p>
      </div>

      {/* CTAs — outer h-12 is the touch target; inner div is the visible button */}
      <div className="flex gap-3 items-center justify-center w-full">
        <button
          type="button"
          onClick={onCancel}
          className="flex h-12 w-[140px] shrink-0 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
        >
          <div className="flex flex-1 flex-col h-[40px] items-center justify-center overflow-hidden rounded-[8px] border border-[#DCDDE0]">
            <span
              className="px-4 py-[10px] text-[#474A4C] whitespace-nowrap"
              style={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.1px',
                fontFamily: 'Roboto, sans-serif',
              }}
            >
              {L.DISCARD_CANCEL}
            </span>
          </div>
        </button>

        <button
          type="button"
          onClick={onConfirm}
          className="flex h-12 w-[140px] shrink-0 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
        >
          <div className="flex flex-1 flex-col h-[40px] items-center justify-center overflow-hidden rounded-[8px] bg-[#2A4455] hover:bg-[#1f3340] transition-colors">
            <span
              className="px-4 py-[10px] text-white whitespace-nowrap"
              style={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.1px',
                fontFamily: 'Roboto, sans-serif',
              }}
            >
              {L.DISCARD_CONFIRM}
            </span>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
);

// ─── Submitted view ────────────────────────────────────────────────────────────

/**
 * Read-only success view shown after the technician submits the form.
 * Uses the local `sections` and `fieldValues` already in component state —
 * no re-fetch needed since the POST response confirms a successful submission.
 */
const SubmittedView: React.FC<{
  sections: FormSection[];
  fieldValues: FieldValues;
  navState: FillTaskFormNavState | undefined;
  onBackToSR: () => void;
}> = ({ sections, fieldValues, navState, onBackToSR }) => (
  <div className="flex flex-col gap-6">
    {/* Success banner */}
    <div className="flex items-start gap-3 p-4 rounded-lg bg-[#E8F7F2] border border-[#2DBA8D]">
      <CheckCircleIcon className="w-5 h-5 text-[#2DBA8D] shrink-0 mt-0.5" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-[#181A1B]">
          {L.SUBMITTED_TITLE}
        </p>
        <p className="text-sm text-[#474A4C]">{L.SUBMITTED_DESC}</p>
      </div>
    </div>

    {/* SR metadata */}
    {navState && (
      <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
        <p className="text-sm font-medium text-[#474A4C]">
          {L.STEP3_SR_SECTION_TITLE}
        </p>
        <div className="grid grid-cols-2 gap-4">
          {navState.serviceRequestDisplayId && (
            <ReadOnlyField
              label={L.LABEL_SR_ID}
              value={navState.serviceRequestDisplayId}
            />
          )}
          {navState.clientName && (
            <ReadOnlyField label={L.LABEL_CLIENT} value={navState.clientName} />
          )}
          {navState.technicianName && (
            <ReadOnlyField
              label={L.LABEL_TECHNICIAN}
              value={navState.technicianName}
            />
          )}
        </div>
      </div>
    )}

    {/* Filled answers */}
    <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-6">
      <p className="text-sm font-medium text-[#474A4C]">
        {L.SUBMITTED_SECTION_TITLE}
      </p>
      {sections.map((section) => (
        <div
          key={section.id}
          className="flex flex-col gap-4 pb-5 border-b border-[#DCDDE0] last:border-0 last:pb-0"
        >
          <p className="text-sm font-medium text-[#181A1B]">{section.name}</p>
          {section.description && (
            <p className="text-xs text-[#474A4C]">{section.description}</p>
          )}
          <div className="grid grid-cols-2 gap-4">
            {section.fields.map((field) => {
              if (field.type === 'attachment') {
                const files = Array.isArray(fieldValues[field.id])
                  ? (fieldValues[field.id] as File[])
                  : [];
                return (
                  <div
                    key={field.id}
                    className="col-span-2 flex flex-col gap-2"
                  >
                    <p className="text-xs font-normal text-[#474A4C]">
                      {field.label}
                    </p>
                    {files.length === 0 ? (
                      <p className="text-sm text-[#181A1B]">
                        {L.STEP3_FIELD_EMPTY}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {files.map((f, i) => (
                          <AttachmentFileRow key={i} file={f} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div
                  key={field.id}
                  className={isFullWidthField(field.type) ? 'col-span-2' : ''}
                >
                  <ReadOnlyField
                    label={field.label}
                    value={formatFieldValueForDisplay(
                      field,
                      fieldValues[field.id]
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>

    <div className="flex justify-end">
      <button
        type="button"
        onClick={onBackToSR}
        className="h-10 px-4 py-2.5 rounded-lg bg-[#2A4455] text-sm font-medium text-white hover:bg-[#1f3340] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
      >
        {L.BUTTON_BACK_TO_SR}
      </button>
    </div>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────

/**
 * Full-page 3-step wizard for technicians (and admins) to fill a task form
 * attached to a service request.
 *
 * Step 1 — read-only SR metadata (auto-filled).
 * Step 2 — controlled form fields based on the locked schema.
 * Step 3 — read-only preview before final submission.
 *
 * On submit the SR transitions to COMPLETED. After a successful submission
 * the component shows a read-only submitted view.
 *
 * @param props - See {@link FillTaskFormProps}.
 */
const FillTaskForm: React.FC<FillTaskFormProps> = ({
  serviceRequestId,
  formId,
  navState,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const srPath = location.pathname.replace(/\/formularz-zadania$/, '');

  // ── Date of Service (read-only, derived from SR scheduled date) ──────────────
  const scheduledStartAtDisplay = (() => {
    if (!navState?.scheduledStartAt) {
      return '';
    }
    const d = new Date(navState.scheduledStartAt);
    return Number.isNaN(d.getTime()) ? '' : formatDateDisplay(d);
  })();

  // ── Equipment state (Step 1) ─────────────────────────────────────────────────
  /** Full item from the fetched list — provides UUID + details for display. */
  const [selectedEquipment, setSelectedEquipment] =
    useState<EquipmentListItem | null>(null);
  /**
   * True when navState.equipmentName is set but the matching item was not found
   * in the fetched list (e.g. list unavailable). Equipment is still considered
   * "confirmed" so the user can proceed; UUID is unknown so it is NOT sent on
   * submission (the SR already has the equipment linked).
   */
  const [equipmentNavPrefill, setEquipmentNavPrefill] = useState(false);
  const [equipmentError, setEquipmentError] = useState('');

  // ── Load state ──────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  const [sections, setSections] = useState<FormSection[]>([]);
  const [submitted, setSubmitted] = useState(false);

  // ── Wizard state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState<FillStep>(1);
  const [maxStep, setMaxStep] = useState<FillStep>(1);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStep2Navigating, setIsStep2Navigating] = useState(false);
  const [techSigUploadBytes, setTechSigUploadBytes] = useState(0);
  const [_techSigUploading, setTechSigUploading] = useState(false);
  const [techSigUploadFailed, setTechSigUploadFailed] = useState(false);
  const [techSigUploadedMedia, setTechSigUploadedMedia] = useState<{
    path: string;
    resourceId: string;
    filename: string;
    fileType: string;
    fileSize: number;
  } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const showSubmitError = (msg: string) => {
    setSubmitError(msg);
  };
  const [showDiscard, setShowDiscard] = useState(false);
  const [showForbiddenSnackbar, setShowForbiddenSnackbar] = useState(false);
  const [pendingNavTarget, setPendingNavTarget] = useState<string | null>(null);

  // ── Attachment upload state ──────────────────────────────────────────────────
  /**
   * Successfully uploaded attachment values keyed by fieldId.
   * filename:size is used to reconcile entries when files are removed.
   */
  const [attachmentUploads, setAttachmentUploads] = useState<
    Record<string, AttachmentValue[]>
  >({});
  /** Real S3 upload progress: fieldId → filename → bytes loaded. */
  const [attachmentProgress, setAttachmentProgress] = useState<
    Record<string, Record<string, number>>
  >({});
  /** Fields with in-flight provisional uploads (blocks "Next" on step 2). */
  const [attachmentUploadingFields, setAttachmentUploadingFields] = useState<
    Set<string>
  >(new Set());
  /** Per-field upload error messages shown below the FileUpload component. */
  const [attachmentFieldErrors, setAttachmentFieldErrors] = useState<
    Record<string, string>
  >({});
  /** Per-field list of filenames that failed to upload. Drives the retry icon in FileUpload. */
  const [attachmentFailedFiles, setAttachmentFailedFiles] = useState<
    Record<string, string[]>
  >({});

  // ── Signature + notes ────────────────────────────────────────────────────────
  const clientSigDataUrlRef = useRef<string | null>(null);
  const [clientSigDataUrl, setClientSigDataUrl] = useState<string | null>(null);
  const [clientSigUploadedMedia, setClientSigUploadedMedia] = useState<{
    path: string;
    resourceId: string;
    filename: string;
    fileType: string;
    fileSize: number;
  } | null>(null);
  const [clientSigUploadFailed, setClientSigUploadFailed] = useState(false);
  const [techSigFile, setTechSigFile] = useState<File | null>(null);
  // Pre-populated from the technician's profile (GET /profile).
  // Used as-is on submission when the technician hasn't selected a new file.
  const [existingTechSigMedia, setExistingTechSigMedia] =
    useState<TechnicianProfileSignature | null>(null);
  // Technician's internal UUID — needed to generate the correct S3 key path
  // when uploading a new signature to their profile.
  const technicianUuidRef = useRef<string | null>(null);
  const [notes, setNotes] = useState('');

  // ── Stable field-change handler ──────────────────────────────────────────────
  const handleFieldChange = useCallback((fieldId: string, val: unknown) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: val }));
    setValidationErrors((prev) => {
      if (!prev[fieldId]) {
        return prev;
      }
      const { [fieldId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Called by FillField whenever the file selection for an attachment field
   * changes. Handles both additions (upload new files → provisional S3) and
   * removals (reconcile attachmentUploads against the new file list).
   *
   * Uses `filename:size` as a stable proxy key to match File objects to
   * their previously uploaded AttachmentValue entries.
   */
  const handleAttachmentFilesChanged = useCallback(
    async (fieldId: string, newFiles: File[]) => {
      const fileKey = (f: File) => `${f.name}:${f.size}`;
      const newFileKeys = new Set(newFiles.map(fileKey));

      // Detect uploads being removed so we can delete them from S3.
      const currentUploads = attachmentUploads[fieldId] ?? [];
      const removedUploads = currentUploads.filter(
        (av) => !newFileKeys.has(`${av.filename}:${av.size}`)
      );
      const keptUploads = currentUploads.filter((av) =>
        newFileKeys.has(`${av.filename}:${av.size}`)
      );

      // Fire-and-forget: delete removed provisional files from S3.
      if (removedUploads.length > 0) {
        void Promise.all(
          removedUploads.map((av) => deleteProvisionalAttachment(av.s3_key))
        );
      }

      setAttachmentUploads((prev) => ({ ...prev, [fieldId]: keptUploads }));

      // Clear progress and failure tracking for removed files.
      setAttachmentProgress((prev) => {
        const kept = Object.fromEntries(
          Object.entries(prev[fieldId] ?? {}).filter(([k]) =>
            newFiles.some((f) => f.name === k)
          )
        );
        return { ...prev, [fieldId]: kept };
      });
      setAttachmentFailedFiles((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] ?? []).filter((name) =>
          newFiles.some((f) => f.name === name)
        ),
      }));

      // Determine which files are newly added (no existing upload record).
      const uploadedKeys = new Set(
        keptUploads.map((av) => `${av.filename}:${av.size}`)
      );
      const addedFiles = newFiles.filter((f) => !uploadedKeys.has(fileKey(f)));
      if (addedFiles.length === 0) {
        return;
      }

      // Pre-populate progress at 0 so the progress bar shows immediately.
      setAttachmentProgress((prev) => ({
        ...prev,
        [fieldId]: {
          ...(prev[fieldId] ?? {}),
          ...Object.fromEntries(addedFiles.map((f) => [f.name, 0])),
        },
      }));

      // Clear any previous field-level upload error.
      setAttachmentFieldErrors((prev) => {
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      });

      setAttachmentUploadingFields((prev) => new Set([...prev, fieldId]));

      // Process in batches of ≤10 (batch endpoint limit).
      const BATCH_SIZE = 10;
      const newlyUploaded: AttachmentValue[] = [];

      for (let i = 0; i < addedFiles.length; i += BATCH_SIZE) {
        const batch = addedFiles.slice(i, i + BATCH_SIZE);
        // Use the MediaKey format: service-requests/{id}/attachments/{uuid}/{filename}
        const paths = batch.map(
          (f) =>
            `service-requests/${serviceRequestId}/attachments/${crypto.randomUUID()}/${f.name}`
        );

        const presignResult = await generateAttachmentPresignedUrls(paths);
        if (!presignResult.success || !presignResult.data) {
          setAttachmentFieldErrors((prev) => ({
            ...prev,
            [fieldId]: 'Nie udało się wygenerować adresów URL do przesyłania.',
          }));
          // Mark all batch files as failed and clear their progress.
          setAttachmentFailedFiles((prev) => ({
            ...prev,
            [fieldId]: [...(prev[fieldId] ?? []), ...batch.map((f) => f.name)],
          }));
          setAttachmentProgress((prev) => {
            const withoutBatch = Object.fromEntries(
              Object.entries(prev[fieldId] ?? {}).filter(
                ([k]) => !batch.some((f) => f.name === k)
              )
            );
            return { ...prev, [fieldId]: withoutBatch };
          });
          setAttachmentUploadingFields((prev) => {
            const next = new Set(prev);
            next.delete(fieldId);
            return next;
          });
          return;
        }

        const entries = presignResult.data.files;
        await Promise.all(
          batch.map(async (file, idx) => {
            const entry = entries[idx];
            if (!entry) {
              return;
            }
            try {
              await uploadFileToS3(entry.url, file, (bytesLoaded) => {
                setAttachmentProgress((prev) => ({
                  ...prev,
                  [fieldId]: {
                    ...(prev[fieldId] ?? {}),
                    [file.name]: bytesLoaded,
                  },
                }));
              });
              newlyUploaded.push({
                filename: file.name,
                s3_key: entry.path,
                size: file.size,
                mime_type: file.type,
              });
              // Clear progress entry on completion.
              setAttachmentProgress((prev) => {
                const { [file.name]: _, ...rest } = prev[fieldId] ?? {};
                return { ...prev, [fieldId]: rest };
              });
            } catch {
              // Mark individual file as failed, clear its progress bar.
              setAttachmentFailedFiles((prev) => ({
                ...prev,
                [fieldId]: [...(prev[fieldId] ?? []), file.name],
              }));
              setAttachmentProgress((prev) => {
                const { [file.name]: _, ...rest } = prev[fieldId] ?? {};
                return { ...prev, [fieldId]: rest };
              });
            }
          })
        );
      }

      if (newlyUploaded.length > 0) {
        setAttachmentUploads((prev) => ({
          ...prev,
          [fieldId]: [...(prev[fieldId] ?? []), ...newlyUploaded],
        }));
      }

      setAttachmentUploadingFields((prev) => {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      });
    },
    [serviceRequestId, attachmentUploads]
  );

  /**
   * Retries uploading a specific failed attachment file.
   * Clears the failure state for that file and re-triggers upload via
   * handleAttachmentFilesChanged (which uploads all files not yet in attachmentUploads).
   */
  const handleAttachmentRetry = useCallback(
    (fieldId: string, filename: string) => {
      setAttachmentFailedFiles((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] ?? []).filter((n) => n !== filename),
      }));
      const currentFiles = (fieldValues[fieldId] as File[] | undefined) ?? [];
      void handleAttachmentFilesChanged(fieldId, currentFiles);
    },
    [fieldValues, handleAttachmentFilesChanged]
  );

  // ── Load form schema ─────────────────────────────────────────────────────────
  useEffect(() => {
    void (async () => {
      if (!formId) {
        setIsLoading(false);
        setLoadError(L.ERROR_LOAD_GENERIC);
        return;
      }
      const [schemaResult, profileResult] = await Promise.all([
        getFormSchema(formId),
        fetchProfile(),
      ]);
      setIsLoading(false);
      if (
        !schemaResult.success ||
        !schemaResult.data ||
        !schemaResult.data.schema?.sections
      ) {
        setLoadError(schemaResult.error ?? L.ERROR_LOAD_GENERIC);
        return;
      }
      setSchemaLoaded(true);
      setSections(
        mapApiSectionsToFormSections(schemaResult.data.schema.sections)
      );
      if (profileResult.success && profileResult.data) {
        const { id, signature } = profileResult.data;
        if (id) {
          technicianUuidRef.current = id;
        }
        if (signature?.isUploaded && signature.filename) {
          setExistingTechSigMedia({
            isUploaded: true,
            path: signature.path ?? '',
            resourceId: signature.resourceId ?? '',
            filename: signature.filename,
            fileType: signature.fileType ?? 'image/png',
            fileSize: signature.fileSize ?? 0,
          });
        }
      }
    })();
  }, [formId, serviceRequestId]);

  // ── Equipment initialisation ─────────────────────────────────────────────────
  // equipment_uuid non-null → equipment was attached to the SR during creation;
  // all details are present in navState — show as read-only prefill.
  // equipment_uuid null → no equipment attached; technician selects from dropdown.
  useEffect(() => {
    if (navState?.equipmentUuid) {
      setEquipmentNavPrefill(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Navigation helpers ───────────────────────────────────────────────────────
  const backToSR = useCallback(() => {
    navigate(srPath);
  }, [navigate, srPath]);

  const requestNavAway = useCallback(
    (target: string) => {
      const isDirty =
        hasAnyValue(fieldValues) ||
        clientSigDataUrl !== null ||
        techSigUploadedMedia !== null ||
        notes.trim() !== '';
      if (step >= 2 && isDirty) {
        setPendingNavTarget(target);
        setShowDiscard(true);
      } else {
        navigate(target);
      }
    },
    [step, fieldValues, clientSigDataUrl, techSigUploadedMedia, notes, navigate]
  );

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    if (pendingNavTarget) {
      navigate(pendingNavTarget);
    }
  };

  const handleDiscardCancel = () => {
    setShowDiscard(false);
    setPendingNavTarget(null);
  };

  // ── Step navigation ──────────────────────────────────────────────────────────
  const handleNextFromStep1 = () => {
    if (!selectedEquipment && !equipmentNavPrefill) {
      setEquipmentError(L.ERROR_EQUIPMENT_REQUIRED);
      return;
    }
    setStep(2);
    setMaxStep((prev) => (prev < 2 ? 2 : prev));
  };

  const handleNextFromStep2 = async () => {
    // Block if any attachment upload is still in flight (has active progress).
    const hasActiveUploads =
      attachmentUploadingFields.size > 0 &&
      [...attachmentUploadingFields].some(
        (fid) => Object.keys(attachmentProgress[fid] ?? {}).length > 0
      );
    if (hasActiveUploads) {
      return;
    }
    const errors = validateStep2(sections, fieldValues, attachmentUploads);
    if (!clientSigDataUrlRef.current) {
      errors['__client_signature'] = L.ERROR_CLIENT_SIGNATURE_REQUIRED;
    }
    if (!techSigUploadedMedia && !existingTechSigMedia) {
      errors['__tech_signature'] = L.ERROR_FIELD_REQUIRED;
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to the first error in DOM order.
      const orderedKeys = [
        ...sections.flatMap((s) => s.fields.map((f) => f.id)),
        '__client_signature',
        '__tech_signature',
      ];
      const firstKey = orderedKeys.find((k) => k in errors);
      if (firstKey) {
        setTimeout(() => {
          document
            .getElementById(`field-${firstKey}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
      return;
    }

    // Upload client signature to S3 once, only if not already uploaded
    // (re-drawing clears clientSigUploadedMedia; going back without redrawing keeps it).
    if (!clientSigUploadedMedia) {
      const dataUrl = clientSigDataUrlRef.current;
      if (!dataUrl) {
        return;
      }
      setClientSigUploadFailed(false);
      setIsStep2Navigating(true);
      try {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'client-signature.png', {
          type: 'image/png',
        });
        const clientSigResourceId = crypto.randomUUID();
        const clientSigFilename = `${(navState?.clientId ?? 'client').replace(/[^a-zA-Z0-9-]/g, '-')}.png`;
        const presignedResult = await generateSignaturePresignedUrls([
          `service-requests/${serviceRequestId}/signatures/${clientSigResourceId}/${clientSigFilename}`,
        ]);
        if (!presignedResult.success || !presignedResult.data) {
          setClientSigUploadFailed(true);
          setIsStep2Navigating(false);
          return;
        }
        const entry = presignedResult.data.files[0];
        if (!entry) {
          setClientSigUploadFailed(true);
          setIsStep2Navigating(false);
          return;
        }
        await uploadFileToS3(entry.url, file, () => {});
        setClientSigUploadedMedia({
          path: entry.path,
          resourceId: clientSigResourceId,
          filename: clientSigFilename,
          fileType: 'image/png',
          fileSize: file.size,
        });
      } catch {
        setClientSigUploadFailed(true);
        setIsStep2Navigating(false);
        return;
      }
    }

    setIsStep2Navigating(false);
    setValidationErrors({});
    setStep(3);
    setMaxStep((prev) => (prev < 3 ? 3 : prev));
  };

  // Clear errors when stepping back so they only appear after clicking Next.
  const handleBackFromStep2 = () => {
    setValidationErrors({});
    setStep(1);
  };
  const handleBackFromStep3 = () => setStep(2);

  const handleStepTabClick = useCallback((num: number) => {
    setStep(num as FillStep);
  }, []);

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setTechSigUploadBytes(0);

    if (!techSigFile && !existingTechSigMedia) {
      showSubmitError(L.ERROR_TECH_SIGNATURE_REQUIRED);
      setIsSubmitting(false);
      return;
    }

    // Technician signature: file was already uploaded at select time; use stored metadata.
    let technicianSignature: SignatureMedia;
    if (techSigUploadedMedia) {
      technicianSignature = {
        isUploaded: true,
        path: techSigUploadedMedia.path,
        resourceId: techSigUploadedMedia.resourceId,
        filename: techSigUploadedMedia.filename,
        fileType: techSigUploadedMedia.fileType,
        fileSize: techSigUploadedMedia.fileSize,
      };
    } else if (existingTechSigMedia) {
      technicianSignature = {
        isUploaded: true,
        path: existingTechSigMedia.path,
        resourceId: existingTechSigMedia.resourceId,
        filename: existingTechSigMedia.filename,
        fileType: existingTechSigMedia.fileType,
        fileSize: existingTechSigMedia.fileSize,
      };
    } else {
      showSubmitError(L.ERROR_TECH_SIGNATURE_REQUIRED);
      setIsSubmitting(false);
      return;
    }

    if (!clientSigUploadedMedia) {
      showSubmitError(L.ERROR_CLIENT_SIGNATURE_REQUIRED);
      setIsSubmitting(false);
      return;
    }
    const clientSignature: SignatureMedia = {
      isUploaded: true,
      path: clientSigUploadedMedia.path,
      resourceId: clientSigUploadedMedia.resourceId,
      filename: clientSigUploadedMedia.filename,
      fileType: clientSigUploadedMedia.fileType,
      fileSize: clientSigUploadedMedia.fileSize,
    };

    const fieldInputs = buildFieldInputs(
      sections,
      fieldValues,
      attachmentUploads
    );
    const result = await submitTaskForm(serviceRequestId, {
      field_inputs: fieldInputs,
      ...(selectedEquipment
        ? { equipment_id: selectedEquipment.id }
        : equipmentNavPrefill && navState?.equipmentUuid
          ? { equipment_id: navState.equipmentUuid }
          : {}),
      client_signature: clientSignature,
      technician_signature: technicianSignature,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
    setIsSubmitting(false);
    if (!result.success) {
      if (result.isForbidden) {
        setShowForbiddenSnackbar(true);
        return;
      }
      showSubmitError(result.error ?? L.ERROR_SUBMIT_GENERIC);
      return;
    }
    setSubmitted(true);
    navigate(`${srPath}?tab=Podsumowanie`, {
      state: { taskFormSubmitted: true },
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const pageHeader = (
    <div className="flex flex-col gap-4 pb-4">
      <h1 className="text-lg font-medium text-[#181A1B]">{L.PAGE_TITLE}</h1>

      {!submitted && (
        <StepTabNav
          steps={FILL_FORM_STEPS}
          currentStep={step}
          maxStep={maxStep}
          onStepClick={handleStepTabClick}
        />
      )}
    </div>
  );

  // ── Reusable skeleton primitives ─────────────────────────────────────────────

  /** A label bar + input bar representing one disabled text field. */
  const FieldSkeleton = ({ half = false }: { half?: boolean }) => (
    <div className="flex flex-col gap-2">
      <div className={`h-3 ${half ? 'w-1/4' : 'w-1/3'} rounded bg-[#DCDDE0]`} />
      <div className="h-10 w-full rounded bg-[#DCDDE0]" />
    </div>
  );

  /** A section heading bar. */
  const HeadingSkeleton = ({ narrow = false }: { narrow?: boolean }) => (
    <div className={`h-4 ${narrow ? 'w-24' : 'w-32'} rounded bg-[#DCDDE0]`} />
  );

  if (isLoading) {
    return (
      <div className="relative w-full h-full flex flex-col bg-white shadow-[0px_2px_4px_0px_#00000014] rounded-[4px] overflow-hidden">
        <div className="shrink-0 w-full px-4 pt-4">
          <div className="max-w-3xl mx-auto">{pageHeader}</div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-8 p-4 max-w-3xl mx-auto animate-pulse">
            {/* ── Podstawowe dane: 5 fields (2-col, last spans 1 col) ── */}
            <div className="flex flex-col gap-4">
              <HeadingSkeleton />
              <div className="grid grid-cols-2 gap-4">
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
            </div>

            <hr className="border-[#DCDDE0]" />

            {/* ── Dane klienta: 4 fields ── */}
            <div className="flex flex-col gap-4">
              <HeadingSkeleton />
              <div className="grid grid-cols-2 gap-4">
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
            </div>

            <hr className="border-[#DCDDE0]" />

            {/* ── Dane placówki: 2 fields + Adres sub-heading + 6 address fields ── */}
            <div className="flex flex-col gap-4">
              <HeadingSkeleton />
              <div className="grid grid-cols-2 gap-4">
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
              <HeadingSkeleton narrow />
              <div className="grid grid-cols-2 gap-4">
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
            </div>

            <hr className="border-[#DCDDE0]" />

            {/* ── Dane aparatury: dropdown + 4 detail fields ── */}
            <div className="flex flex-col gap-4">
              <HeadingSkeleton />
              <div className="grid grid-cols-2 gap-4">
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
              </div>
            </div>
          </div>
        </div>
        {/* Footer visible during shimmer — CTAs disabled until schema loads */}
        <div className="absolute bottom-4 left-4 right-4 z-10 bg-[#FAFAFA] rounded-[20px] shadow-[0px_2px_8px_rgba(0,0,0,0.08),0px_6px_12px_4px_rgba(0,0,0,0.04),0px_-2px_8px_rgba(0,0,0,0.08)]">
          <div className="max-w-3xl mx-auto px-3 py-2 flex items-center justify-between">
            <div className="opacity-0 pointer-events-none">
              <button
                type="button"
                className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C]"
              >
                {L.BUTTON_CANCEL}
              </button>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled
                className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C] opacity-50 cursor-not-allowed"
              >
                {L.BUTTON_CANCEL}
              </button>
              <button
                type="button"
                disabled
                className="h-10 px-4 py-2.5 rounded-lg bg-[#2A4455] text-sm font-medium text-white opacity-50 cursor-not-allowed"
              >
                {L.BUTTON_NEXT}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2 field-level skeleton (used when schema loads after navigation) ──
  const Step2Skeleton = () => (
    <div className="flex flex-col gap-8 animate-pulse">
      {Array.from({ length: 2 }).map((_, si) => (
        <React.Fragment key={si}>
          {si > 0 && <hr className="border-[#DCDDE0]" />}
          <div className="flex flex-col gap-4">
            {/* Section heading + optional description */}
            <div className="flex flex-col gap-1.5">
              <div className="h-4 w-36 rounded bg-[#DCDDE0]" />
              <div className="h-3 w-1/2 rounded bg-[#DCDDE0]" />
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              {/* Half-width fields */}
              <FieldSkeleton />
              <FieldSkeleton />
              <FieldSkeleton />
              <FieldSkeleton />
              {/* Full-width field (textarea / attachment) */}
              <div className="col-span-2 flex flex-col gap-2">
                <div className="h-3 w-1/4 rounded bg-[#DCDDE0]" />
                <div className="h-24 w-full rounded bg-[#DCDDE0]" />
              </div>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  if (loadError) {
    return <ErrorPage403 onBack={backToSR} />;
  }

  if (!schemaLoaded) {
    return (
      <div className="w-full h-full overflow-y-auto bg-white shadow-[0px_2px_4px_0px_#00000014] rounded-[4px]">
        <div className="flex flex-col gap-4 p-4 max-w-2xl mx-auto">
          {pageHeader}
          <div className="p-4 rounded-lg bg-[#FFF7E0] border border-[#FF9800] text-sm text-[#181A1B]">
            {L.ERROR_NO_SCHEMA}
          </div>
          <button
            type="button"
            onClick={backToSR}
            className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#181A1B] hover:bg-[#F3F4F6] transition-colors self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
          >
            {L.BUTTON_BACK_TO_SR}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col bg-white shadow-[0px_2px_4px_0px_#00000014] rounded-[4px] overflow-hidden">
      {/* Pinned header: page title + stepper */}
      <div className="shrink-0 w-full px-4 pt-4">
        <div className="max-w-3xl mx-auto">{pageHeader}</div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-6 p-4 pb-28 max-w-3xl mx-auto">
          {/* Already submitted */}
          {submitted ? (
            <SubmittedView
              sections={sections}
              fieldValues={fieldValues}
              navState={navState}
              onBackToSR={backToSR}
            />
          ) : (
            <>
              {/* ── Step 1: SR details ───────────────────────────────────────────── */}
              {step === 1 && (
                <div className="flex flex-col gap-8">
                  {/* ── Podstawowe dane ── */}
                  <div className="flex flex-col gap-4">
                    <p className="text-base font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_BASIC}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <DisabledTextField
                        label={L.LABEL_FORM_ID}
                        value={navState?.formDisplayId ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_SR_ID}
                        value={navState?.serviceRequestDisplayId ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_TECHNICIAN_ID}
                        value={navState?.technicianId ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_TECHNICIAN_NAME}
                        value={navState?.technicianName ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_DATE_OF_SERVICE}
                        value={scheduledStartAtDisplay}
                        showCalendarIcon
                      />
                    </div>
                  </div>

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Dane klienta ── */}
                  <div className="flex flex-col gap-4">
                    <p className="text-base font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_CLIENT}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <DisabledTextField
                        label={L.LABEL_CLIENT_ID}
                        value={navState?.clientId ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_CLIENT_NAME}
                        value={navState?.clientName ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_CLIENT_EMAIL}
                        value={navState?.clientEmail ?? ''}
                      />
                      <DisabledPhoneField
                        label={L.LABEL_CLIENT_PHONE}
                        value={navState?.clientPhoneNumber ?? ''}
                      />
                    </div>
                  </div>

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Dane placówki ── */}
                  <div className="flex flex-col gap-4">
                    <p className="text-base font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_FACILITY}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <DisabledTextField
                        label={L.LABEL_FACILITY_ID}
                        value={navState?.facilityId ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_FACILITY_NAME}
                        value={navState?.facilityName ?? ''}
                      />
                    </div>

                    {/* ── Dane adresowe (sub-section) ── */}
                    <p className="text-sm font-medium text-[#474A4C] mt-2">
                      {L.STEP1_SECTION_ADDRESS}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <DisabledTextField
                        label={L.LABEL_ADDRESS_LINE1}
                        value={navState?.facilityAddressLine1 ?? ''}
                      />
                      <DisabledTextField
                        label={`${L.LABEL_ADDRESS_LINE2} (Opcjonalne)`}
                        value={navState?.facilityAddressLine2 ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_CITY}
                        value={navState?.facilityCity ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_STATE}
                        value={navState?.facilityState ?? ''}
                      />
                      <DisabledTextField
                        label={`${L.LABEL_POSTAL_CODE} (Opcjonalne)`}
                        value={navState?.facilityPostalCode ?? ''}
                      />
                      <DisabledTextField
                        label={L.LABEL_COUNTRY}
                        value={navState?.facilityCountry ?? ''}
                      />
                    </div>
                  </div>

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Dane aparatury ── */}
                  <div className="flex flex-col gap-4">
                    <p className="text-base font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_EQUIPMENT}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Equipment name dropdown — mandatory */}
                      <div className="flex flex-col gap-1">
                        {equipmentNavPrefill ? (
                          /* Pre-filled from SR — show as read-only field */
                          <DisabledTextField
                            label={L.LABEL_EQUIPMENT_NAME}
                            value={navState?.equipmentName ?? ''}
                          />
                        ) : (
                          <EquipmentDropdown
                            clientId={navState?.clientUuid ?? ''}
                            facilityId={navState?.facilityUuid ?? ''}
                            selectedId={selectedEquipment?.id ?? ''}
                            {...(selectedEquipment?.name
                              ? { selectedName: selectedEquipment.name }
                              : {})}
                            label={L.LABEL_EQUIPMENT_NAME}
                            onSelect={(eq) => {
                              setSelectedEquipment(eq);
                              setEquipmentError('');
                            }}
                            error={!!equipmentError}
                            errorMessage={equipmentError || undefined}
                          />
                        )}
                      </div>

                      {/* Equipment details — shown once an item is selected or pre-filled from SR */}
                      {(selectedEquipment ?? equipmentNavPrefill) && (
                        <>
                          <DisabledTextField
                            label={L.LABEL_EQUIPMENT_ID_DISPLAY}
                            value={
                              selectedEquipment?.equipmentId ??
                              navState?.equipmentId ??
                              ''
                            }
                          />
                          <DisabledTextField
                            label={L.LABEL_EQUIPMENT_CATEGORY}
                            value={
                              selectedEquipment?.categoryLabel ??
                              selectedEquipment?.category ??
                              navState?.equipmentCategoryLabel ??
                              navState?.equipmentCategory ??
                              ''
                            }
                          />
                          <DisabledTextField
                            label={`${L.LABEL_EQUIPMENT_MODEL} (Opcjonalne)`}
                            value={
                              selectedEquipment?.modelName ??
                              navState?.equipmentModelName ??
                              ''
                            }
                          />
                          <DisabledTextField
                            label={`${L.LABEL_EQUIPMENT_SERIAL} (Opcjonalne)`}
                            value={
                              selectedEquipment?.serialNumber ??
                              navState?.equipmentSerialNumber ??
                              ''
                            }
                          />
                          <DisabledTextField
                            label={`${L.LABEL_EQUIPMENT_MANUFACTURE_DATE} (Opcjonalne)`}
                            value={
                              selectedEquipment?.dateOfProduction ??
                              navState?.equipmentDateOfProduction ??
                              ''
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Form fields ──────────────────────────────────────────── */}
              {step === 2 && (
                <div className="flex flex-col gap-8">
                  {sections.length === 0 && <Step2Skeleton />}
                  {sections.map((section, idx) => (
                    <React.Fragment key={section.id}>
                      {idx > 0 && <hr className="border-[#DCDDE0]" />}
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <p className="text-base font-medium text-[#181A1B]">
                            {section.name}
                          </p>
                          {section.description && (
                            <p className="text-sm text-[#474A4C]">
                              {section.description}
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                          {section.fields.map((field) => (
                            <div
                              key={field.id}
                              id={`field-${field.id}`}
                              className={
                                isFullWidthField(field.type) ? 'col-span-2' : ''
                              }
                            >
                              <FillField
                                field={field}
                                value={fieldValues[field.id]}
                                onFieldChange={handleFieldChange}
                                onAttachmentFilesChanged={
                                  handleAttachmentFilesChanged
                                }
                                uploadProgress={
                                  attachmentProgress[field.id] ?? {}
                                }
                                {...(attachmentFailedFiles[field.id]?.length
                                  ? {
                                      failedFiles:
                                        attachmentFailedFiles[field.id],
                                    }
                                  : {})}
                                onRetry={(filename) =>
                                  handleAttachmentRetry(field.id, filename)
                                }
                                {...(validationErrors[field.id]
                                  ? { error: validationErrors[field.id] }
                                  : {})}
                                {...(attachmentFieldErrors[field.id]
                                  ? { error: attachmentFieldErrors[field.id] }
                                  : {})}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Client Signature ─────────────────────────────────────── */}
                  <div
                    id="field-__client_signature"
                    className="flex flex-col gap-4"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="text-base font-medium text-[#181A1B]">
                        {L.SECTION_CLIENT_SIGNATURE}
                      </p>
                    </div>
                    <ClientSignatureCanvas
                      onCapture={(dataUrl) => {
                        clientSigDataUrlRef.current = dataUrl;
                        setClientSigDataUrl(dataUrl);
                        setClientSigUploadFailed(false);
                        setClientSigUploadedMedia(null);
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next['__client_signature'];
                          return next;
                        });
                      }}
                      onClear={() => {
                        clientSigDataUrlRef.current = null;
                        setClientSigDataUrl(null);
                        setClientSigUploadedMedia(null);
                        setClientSigUploadFailed(false);
                      }}
                      error={
                        !!validationErrors['__client_signature'] ||
                        clientSigUploadFailed
                      }
                      errorMessage={
                        clientSigUploadFailed
                          ? L.ERROR_SIGNATURE_UPLOAD_FAILED
                          : (validationErrors['__client_signature'] ??
                            undefined)
                      }
                      existingDataUrl={clientSigDataUrl ?? undefined}
                    />
                  </div>

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Technician's Signature ───────────────────────────────────── */}
                  <div
                    id="field-__tech_signature"
                    className="flex flex-col gap-4"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="text-base font-medium text-[#181A1B]">
                        {L.SECTION_TECH_SIGNATURE}
                        <span className="text-[#E53935] ml-0.5">*</span>
                      </p>
                    </div>
                    {existingTechSigMedia && !techSigFile && (
                      <div className="flex items-center gap-3 p-4 bg-white rounded">
                        <img
                          src={getFileIconUrl(existingTechSigMedia.filename)}
                          alt=""
                          className="w-10 h-10 shrink-0"
                        />
                        <div className="flex flex-col flex-1 min-w-0">
                          <p className="text-sm text-[#181A1B] truncate">
                            {existingTechSigMedia.filename}
                          </p>
                          <p className="text-xs text-[#878B8E]">
                            {formatFileSize(existingTechSigMedia.fileSize)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExistingTechSigMedia(null)}
                          aria-label="Usuń podpis"
                          className="shrink-0 rounded focus:outline-none cursor-pointer"
                        >
                          <Trash2
                            size={24}
                            className="sm:w-[20px] sm:h-[20px] text-[#E11B1B]"
                          />
                        </button>
                      </div>
                    )}
                    {(!existingTechSigMedia || techSigFile) && (
                      <FileUpload
                        onFileSelect={async (files) => {
                          const file = files[0] ?? null;
                          setTechSigFile(file);
                          setTechSigUploadedMedia(null);
                          setTechSigUploadFailed(false);
                          setTechSigUploadBytes(0);
                          if (!file) {
                            return;
                          }
                          setValidationErrors((prev) => {
                            const next = { ...prev };
                            delete next['__tech_signature'];
                            return next;
                          });
                          setTechSigUploading(true);
                          const techSigResourceId = crypto.randomUUID();
                          const techUuid = technicianUuidRef.current;
                          const sigPath = techUuid
                            ? `technicians/${techUuid}/signatures/${techSigResourceId}/${file.name}`
                            : `service-requests/${serviceRequestId}/signatures/${techSigResourceId}/${file.name}`;
                          const presignedResult =
                            await generateProfileSignaturePresignedUrl([
                              sigPath,
                            ]);
                          if (
                            !presignedResult.success ||
                            !presignedResult.data
                          ) {
                            setTechSigUploading(false);
                            setTechSigUploadFailed(true);
                            return;
                          }
                          const entry = presignedResult.data.files[0];
                          if (!entry) {
                            setTechSigUploading(false);
                            setTechSigUploadFailed(true);
                            return;
                          }
                          try {
                            await uploadFileToS3(
                              entry.url,
                              file,
                              (bytesLoaded) => {
                                setTechSigUploadBytes(bytesLoaded);
                              }
                            );
                            const uploadedMedia = {
                              path: entry.path,
                              resourceId: techSigResourceId,
                              filename: file.name,
                              fileType: file.type,
                              fileSize: file.size,
                            };
                            setTechSigUploadedMedia(uploadedMedia);
                            void updateProfileSignature({
                              isUploaded: true,
                              ...uploadedMedia,
                            });
                          } catch {
                            setTechSigUploadFailed(true);
                          } finally {
                            setTechSigUploading(false);
                          }
                        }}
                        selectedFiles={techSigFile ? [techSigFile] : []}
                        allowedTypes={[
                          FileType.JPG,
                          FileType.JPEG,
                          FileType.PNG,
                          FileType.PDF,
                        ]}
                        maxFiles={1}
                        hideMaxFilesMessage
                        fullWidth
                        error={
                          !!validationErrors['__tech_signature'] ||
                          techSigUploadFailed
                        }
                        errorMessage={
                          techSigUploadFailed
                            ? L.ERROR_SIGNATURE_UPLOAD_FAILED
                            : validationErrors['__tech_signature']
                        }
                      />
                    )}
                  </div>

                  <hr className="border-[#DCDDE0]" />

                  {/* ── Dodaj notatki ────────────────────────────────────────────── */}
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                      <p className="text-base font-medium text-[#181A1B]">
                        {L.SECTION_NOTES}
                      </p>
                    </div>
                    <Textarea
                      label={
                        <>
                          {L.NOTES_LABEL}{' '}
                          <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
                        </>
                      }
                      value={notes}
                      onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                      maxLength={500}
                      rows={4}
                      fullWidth
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              {/* ── Step 3: Preview ──────────────────────────────────────────────── */}
              {step === 3 && (
                <div className="relative flex flex-col gap-6">
                  {isSubmitting && (
                    <div className="absolute inset-0 z-10 rounded pointer-events-all bg-white/60">
                      {techSigFile && techSigUploadBytes > 0 && (
                        <div className="flex flex-col items-center justify-center h-full gap-2">
                          <p className="text-sm font-medium text-[#474A4C]">
                            Przesyłanie podpisu…{' '}
                            {Math.min(
                              100,
                              Math.round(
                                (techSigUploadBytes / techSigFile.size) * 100
                              )
                            )}
                            %
                          </p>
                          <div className="w-48 h-1.5 bg-[#DCDDE0] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#2A4455] transition-all duration-150"
                              style={{
                                width: `${Math.min(100, Math.round((techSigUploadBytes / techSigFile.size) * 100))}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <h2 className="text-base font-medium text-[#181A1B]">
                      {L.STEP3_TITLE}
                    </h2>
                    {L.STEP3_DESC && (
                      <p className="text-sm text-[#474A4C]">{L.STEP3_DESC}</p>
                    )}
                  </div>

                  {/* Podstawowe dane summary card */}
                  <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-medium text-[#474A4C]">
                      {L.STEP3_SR_SECTION_TITLE}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <ReadOnlyField
                        label={L.LABEL_FORM_ID}
                        value={navState?.formDisplayId ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_SR_ID}
                        value={
                          navState?.serviceRequestDisplayId ??
                          L.VALUE_NOT_ASSIGNED
                        }
                      />
                      <ReadOnlyField
                        label={L.LABEL_TECHNICIAN_ID}
                        value={navState?.technicianId ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_TECHNICIAN_NAME}
                        value={navState?.technicianName ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_DATE_OF_SERVICE}
                        value={scheduledStartAtDisplay || L.VALUE_NOT_ASSIGNED}
                      />
                    </div>
                  </div>

                  {/* Dane klienta summary */}
                  <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_CLIENT}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <ReadOnlyField
                        label={L.LABEL_CLIENT_ID}
                        value={navState?.clientId ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_CLIENT_NAME}
                        value={navState?.clientName ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_CLIENT_EMAIL}
                        value={navState?.clientEmail ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_CLIENT_PHONE}
                        value={
                          navState?.clientPhoneNumber ?? L.VALUE_NOT_ASSIGNED
                        }
                      />
                    </div>
                  </div>

                  {/* Dane placówki summary */}
                  <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_FACILITY}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <ReadOnlyField
                        label={L.LABEL_FACILITY_ID}
                        value={navState?.facilityId ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_FACILITY_NAME}
                        value={navState?.facilityName ?? L.VALUE_NOT_ASSIGNED}
                      />
                    </div>
                  </div>

                  {/* Dane adresowe summary */}
                  <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-medium text-[#474A4C]">
                      {L.STEP1_SECTION_ADDRESS}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <ReadOnlyField
                        label={L.LABEL_ADDRESS_LINE1}
                        value={
                          navState?.facilityAddressLine1 ?? L.VALUE_NOT_ASSIGNED
                        }
                      />
                      <ReadOnlyField
                        label={L.LABEL_ADDRESS_LINE2}
                        value={
                          navState?.facilityAddressLine2 ?? L.VALUE_NOT_ASSIGNED
                        }
                      />
                      <ReadOnlyField
                        label={L.LABEL_CITY}
                        value={navState?.facilityCity ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_STATE}
                        value={navState?.facilityState ?? L.VALUE_NOT_ASSIGNED}
                      />
                      <ReadOnlyField
                        label={L.LABEL_POSTAL_CODE}
                        value={
                          navState?.facilityPostalCode ?? L.VALUE_NOT_ASSIGNED
                        }
                      />
                    </div>
                  </div>

                  {/* Dane aparatury summary */}
                  {(selectedEquipment !== null || equipmentNavPrefill) && (
                    <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                      <p className="text-sm font-medium text-[#474A4C]">
                        {L.STEP1_SECTION_EQUIPMENT}
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <ReadOnlyField
                          label={L.LABEL_EQUIPMENT_NAME}
                          value={
                            selectedEquipment?.name ??
                            navState?.equipmentName ??
                            L.VALUE_NOT_ASSIGNED
                          }
                        />
                        <ReadOnlyField
                          label={L.LABEL_EQUIPMENT_ID_DISPLAY}
                          value={
                            selectedEquipment?.equipmentId ??
                            navState?.equipmentId ??
                            L.VALUE_NOT_ASSIGNED
                          }
                        />
                        <ReadOnlyField
                          label={L.LABEL_EQUIPMENT_CATEGORY}
                          value={
                            selectedEquipment?.categoryLabel ??
                            selectedEquipment?.category ??
                            navState?.equipmentCategoryLabel ??
                            navState?.equipmentCategory ??
                            L.VALUE_NOT_ASSIGNED
                          }
                        />
                        {(selectedEquipment?.modelName ??
                          navState?.equipmentModelName) && (
                          <ReadOnlyField
                            label={L.LABEL_EQUIPMENT_MODEL}
                            value={
                              selectedEquipment?.modelName ??
                              navState?.equipmentModelName ??
                              ''
                            }
                          />
                        )}
                        {(selectedEquipment?.serialNumber ??
                          navState?.equipmentSerialNumber) && (
                          <ReadOnlyField
                            label={L.LABEL_EQUIPMENT_SERIAL}
                            value={
                              selectedEquipment?.serialNumber ??
                              navState?.equipmentSerialNumber ??
                              ''
                            }
                          />
                        )}
                        {(selectedEquipment?.dateOfProduction ??
                          navState?.equipmentDateOfProduction) && (
                          <ReadOnlyField
                            label={L.LABEL_EQUIPMENT_MANUFACTURE_DATE}
                            value={
                              selectedEquipment?.dateOfProduction ??
                              navState?.equipmentDateOfProduction ??
                              ''
                            }
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Filled form values */}
                  {sections.map((section) => (
                    <div
                      key={section.id}
                      className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4"
                    >
                      <p className="text-sm font-medium text-[#474A4C]">
                        {section.name}
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {section.fields.map((field) => {
                          if (field.type === 'attachment') {
                            const files = Array.isArray(fieldValues[field.id])
                              ? (fieldValues[field.id] as File[])
                              : [];
                            return (
                              <div
                                key={field.id}
                                className="col-span-2 flex flex-col gap-2"
                              >
                                <p className="text-xs font-normal text-[#474A4C]">
                                  {field.label}
                                </p>
                                {files.length === 0 ? (
                                  <p className="text-sm text-[#181A1B]">
                                    {L.STEP3_FIELD_EMPTY}
                                  </p>
                                ) : (
                                  <div className="flex flex-col gap-2">
                                    {files.map((f, i) => (
                                      <AttachmentFileRow key={i} file={f} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          if (field.type === 'number_range') {
                            const rawStr =
                              typeof fieldValues[field.id] === 'string'
                                ? (fieldValues[field.id] as string)
                                : '';
                            const numericValue =
                              rawStr === '' ? null : parseFloat(rawStr);
                            return (
                              <div
                                key={field.id}
                                className="col-span-2 flex flex-col gap-3"
                              >
                                <ReadOnlyField
                                  label={field.label}
                                  value={formatFieldValueForDisplay(
                                    field,
                                    fieldValues[field.id]
                                  )}
                                />
                                <GaugeDisplay
                                  value={numericValue}
                                  min={field.min}
                                  max={field.max}
                                  unit={field.unit}
                                />
                              </div>
                            );
                          }
                          return (
                            <div
                              key={field.id}
                              className={
                                isFullWidthField(field.type) ? 'col-span-2' : ''
                              }
                            >
                              <ReadOnlyField
                                label={field.label}
                                value={formatFieldValueForDisplay(
                                  field,
                                  fieldValues[field.id]
                                )}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Client signature preview */}
                  {clientSigDataUrl && (
                    <div className="border border-[#DCDDE0] rounded-lg p-4 flex flex-col gap-4">
                      <p className="text-lg font-medium text-[#474A4C]">
                        {L.SECTION_CLIENT_SIGNATURE}
                      </p>
                      <div
                        style={{
                          height: 228,
                          borderRadius: 26,
                          border: '1px solid #878B8E',
                          background: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 12,
                        }}
                      >
                        <img
                          src={clientSigDataUrl}
                          alt={L.SECTION_CLIENT_SIGNATURE}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'fill',
                            display: 'block',
                          }}
                        />
                      </div>
                      <p className="text-xs px-4" style={{ color: '#474A4C' }}>
                        {L.CLIENT_SIGNATURE_HELPER}
                      </p>
                    </div>
                  )}

                  {/* Technician signature preview */}
                  {techSigFile && (
                    <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                      <p className="text-sm font-medium text-[#474A4C]">
                        {L.SECTION_TECH_SIGNATURE}
                      </p>
                      <div className="flex items-center gap-3">
                        <img
                          src={getFileIconUrl(techSigFile.name)}
                          alt=""
                          aria-hidden="true"
                          className="w-10 h-10 shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <p className="text-sm text-[#2A4455] truncate">
                            {techSigFile.name}
                          </p>
                          <p className="text-xs text-[#878B8E]">
                            {formatFileSize(techSigFile.size)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes preview */}
                  {notes.trim() && (
                    <div className="border border-[#DCDDE0] rounded-lg p-5 flex flex-col gap-4">
                      <p className="text-sm font-medium text-[#474A4C]">
                        {L.SECTION_NOTES}
                      </p>
                      <p className="text-sm text-[#181A1B] whitespace-pre-wrap break-all">
                        {notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Discard dialog */}
          {showDiscard && (
            <DiscardDialog
              onCancel={handleDiscardCancel}
              onConfirm={handleDiscardConfirm}
            />
          )}

          {/* Permission revoked snackbar */}
          {showForbiddenSnackbar && (
            <Snackbar
              open
              message={L.ERROR_SUBMIT_FORBIDDEN}
              variant="error"
              position="top-center"
              onClose={() => setShowForbiddenSnackbar(false)}
            />
          )}
          {/* Submit error snackbar */}
          {submitError && (
            <Snackbar
              open
              message={submitError}
              variant="error"
              position="top-center"
              onClose={() => setSubmitError(null)}
            />
          )}
        </div>
        {/* end max-w-3xl content */}
      </div>
      {/* end flex-1 overflow-y-auto */}

      {/* ── Pinned footer bar ─────────────────────────────────────────────── */}
      {!submitted && (
        <div className="absolute bottom-4 left-4 right-4 z-10 bg-[#FAFAFA] rounded-[20px] shadow-[0px_2px_8px_rgba(0,0,0,0.08),0px_6px_12px_4px_rgba(0,0,0,0.04),0px_-2px_8px_rgba(0,0,0,0.08)]">
          <div className="max-w-3xl mx-auto px-3 py-2 flex items-center justify-between">
            {/* Left: invisible spacer on step 1; Anuluj on steps 2/3 */}
            <div className={step === 1 ? 'opacity-0 pointer-events-none' : ''}>
              <button
                type="button"
                onClick={() => requestNavAway(srPath)}
                className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C] hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                data-testid="fill-form-cancel-left"
              >
                {L.BUTTON_CANCEL}
              </button>
            </div>

            {/* Right: Anuluj+Dalej on step 1; Wstecz+primary on steps 2/3 */}
            <div className="flex items-center gap-4">
              {step === 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => requestNavAway(srPath)}
                    className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C] hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-cancel"
                  >
                    {L.BUTTON_CANCEL}
                  </button>
                  <button
                    type="button"
                    onClick={handleNextFromStep1}
                    className="h-10 px-4 py-2.5 rounded-lg bg-[#2A4455] text-sm font-medium text-white hover:bg-[#1f3340] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-next-step1"
                  >
                    {L.BUTTON_NEXT}
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button
                    type="button"
                    onClick={handleBackFromStep2}
                    disabled={isSubmitting || isStep2Navigating}
                    className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C] hover:bg-[#F3F4F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-back"
                  >
                    {L.BUTTON_BACK}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleNextFromStep2();
                    }}
                    disabled={
                      isStep2Navigating ||
                      (attachmentUploadingFields.size > 0 &&
                        [...attachmentUploadingFields].some(
                          (fid) =>
                            Object.keys(attachmentProgress[fid] ?? {}).length >
                            0
                        ))
                    }
                    className="h-10 px-4 py-2.5 rounded-lg bg-[#2A4455] text-sm font-medium text-white hover:bg-[#1f3340] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-next-step2"
                  >
                    {isStep2Navigating ? L.BUTTON_PREVIEWING : L.BUTTON_PREVIEW}
                  </button>
                </>
              )}
              {step === 3 && (
                <>
                  <button
                    type="button"
                    onClick={handleBackFromStep3}
                    disabled={isSubmitting}
                    className="h-10 px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-sm font-medium text-[#474A4C] hover:bg-[#F3F4F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-back"
                  >
                    {L.BUTTON_BACK}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleSubmit();
                    }}
                    disabled={isSubmitting}
                    className="h-10 px-4 py-2.5 rounded-lg bg-[#2A4455] text-sm font-medium leading-5 tracking-[0.1px] break-words text-white font-['Roboto'] hover:bg-[#1f3340] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    data-testid="fill-form-submit"
                  >
                    {isSubmitting ? L.BUTTON_SUBMITTING : L.BUTTON_SUBMIT}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FillTaskForm;
